import type { ReactNode } from "react";

import { Button } from "./Button";
import { StatusPill } from "./StatusPill";

/**
 * "Unsaved changes", said in words and with a dot — never by colour alone — and only while `dirty`. Always drawn
 * as a `role="status"` region, so that a screen reader hears it when it fills. It is the ONE place that
 * says it: the bar under a form draws it, and so does a small form that has no bar (a card of
 * third-party credentials, the pool editor).
 */
export function UnsavedBadge({ dirty }: { dirty: boolean }) {
  return <span role="status">{dirty && <StatusPill tone="warning">Unsaved changes</StatusPill>}</span>;
}

/**
 * The bar under an EDITING form — the same on every one of them, so that "how do I save this, and
 * what happens to what I typed?" is answered in the same place every time:
 *
 *     [ the error of the save, when it failed ]
 *     ● Unsaved changes  [extra action]                              [ Cancel ] [ Save ]
 *
 * It is STICKY: it sticks to the bottom of the page that scrolls while the form is longer than the
 * window (the stage form is 900 lines), and rests under the form when its end is in view — so the
 * primary action, the way out and the failure of a save are always in reach, never a scroll away.
 * It goes INSIDE the `<form>`, after the card that holds the fields, so that the primary button is
 * the form's submit button (Enter in a field saves) — or pass `onSubmit` for a page that is not a
 * `<form>`.
 *
 *   dirty      the form holds something the server does not (`useDirtyValues`): the pill says so, in
 *              words and with a dot, never by colour alone. The bar does not hold the navigation by
 *              itself: the page passes the same `dirty` to `useUnsavedChanges`, so that Cancel — which
 *              leaves the page — asks first, like every other way out.
 *   error      where the failure of THIS form's save is said (`<MutationError>`): beside the button
 *              that failed, in view, and only ever this form's — a failure of another action of the
 *              page has its own slot next to its own button. Capped in height and scrollable, so a
 *              long technical detail never pushes the buttons off the window.
 *   children   the page's other actions, on the left (a "Close item").
 *
 * The bar's height does not change when `dirty` does. It has no `className`: a bar is a bar.
 */
export function FormActionBar({
  dirty,
  submitLabel,
  pendingLabel = "Saving…",
  pending = false,
  disabled = false,
  disabledReason,
  onCancel,
  onSubmit,
  error,
  children,
}: {
  dirty: boolean;
  /** What the primary action says: "Save", "Create component". */
  submitLabel: string;
  /** What it says while it runs. */
  pendingLabel?: string;
  pending?: boolean;
  /** The action cannot run yet (a required value is missing, a JSON block does not parse). */
  disabled?: boolean;
  /** Why, as a tooltip. */
  disabledReason?: string;
  onCancel: () => void;
  /** Only for a page that is not a `<form>`; inside one, the primary button submits it. */
  onSubmit?: () => void;
  error?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="sticky bottom-0 z-10 mt-6 border-t border-line bg-ground py-3">
      {/* Always drawn: `error` is usually a `<MutationError>`, which draws nothing until the save fails,
          and an empty slot must not leave its margin behind. */}
      <div className="mb-3 max-h-40 overflow-y-auto empty:hidden">{error}</div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-h-9 flex-wrap items-center gap-2">
          <UnsavedBadge dirty={dirty} />
          {children}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type={onSubmit ? "button" : "submit"}
            onClick={onSubmit}
            loading={pending}
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
          >
            {pending ? pendingLabel : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
